import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Maximize, Minimize, Bookmark, Loader2 } from "lucide-react";
import { CourseSidebarLecture, LectureContent } from "../types/lectureTypes";
import HtmlRenderer from "@/components/ui/htmlRenderer";
import { BookmarkModal } from "./bookmarkModal";
import { BookmarkGroupData } from "@/routes/bookmarks/types/bookmarkGroupData";
import { NextLectureButton } from "./nextLectureButton";
import { Await } from "react-router-dom";

interface LectureViewWithPermissionProps {
  content: LectureContent;
  htmlContentPromise: Promise<string>;
  lectureId: string;
  hasFullAccess: boolean;
  isCompleted: boolean | null;
  nextLecture: CourseSidebarLecture | null;
  bookmarkContents: BookmarkGroupData[];
  courseProgress: number | null;
}

export function LectureViewWithPermission({
  content,
  nextLecture,
  htmlContentPromise,
  isCompleted,
  courseProgress,
  hasFullAccess,
  bookmarkContents,
}: LectureViewWithPermissionProps) {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBookmarkDialogOpen, setIsBookmarkDialogOpen] = useState(false);
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-black">
        {/* Fullscreen Content */}
        <div className="h-full w-full flex flex-col">
          {/* Minimal Header */}
          <div className="bg-primary px-4 py-2 flex items-center justify-between">
            <h2 className="text-white text-sm font-medium truncate max-w-xl">
              {content.name}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFullscreen(false)}
              className="text-white hover:bg-black-800"
            >
              <Minimize className="w-4 h-4 mr-2" />
              {t("lecture.view.normalScreen")}
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden bg-white">
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center h-full py-20">
                  <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                  <p className="mt-4 text-gray-600 dark:text-gray-400">
                    {t("lecture.view.loading")}
                  </p>
                </div>
              }
            >
              <Await resolve={htmlContentPromise}>
                {(htmlContent) => <HtmlRenderer htmlString={htmlContent} />}
              </Await>
            </Suspense>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Content Container - Full height, no scroll on outer div */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* HTML Content - Scrollable */}
        <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-800">
          <Suspense
            fallback={
              <div className="flex flex-col items-center justify-center h-full py-20">
                <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                <p className="mt-4 text-gray-600 dark:text-gray-400">
                  {t("lecture.view.loading")}
                </p>
              </div>
            }
          >
            <Await resolve={htmlContentPromise}>
              {(htmlContent) => <HtmlRenderer htmlString={htmlContent} />}
            </Await>
          </Suspense>
        </div>

        {/* Bottom Action Bar - Fixed at bottom */}
        <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left Side - Title */}
            <div className="flex-1 min-w-0 mr-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {content.name}
              </h2>
              {content.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate mt-1">
                  {content.description}
                </p>
              )}
            </div>

            {/* Right Side - Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <>
                <button
                  onClick={() => setIsBookmarkDialogOpen(true)}
                  className="p-2 rounded hover:bg-primary hover:text-white transition-colors"
                >
                  <Bookmark className="w-4 h-4" />
                </button>

                <BookmarkModal
                  bookmarkContents={bookmarkContents}
                  isOpen={isBookmarkDialogOpen}
                  onClose={() => setIsBookmarkDialogOpen(false)}
                />

                {hasFullAccess && (
                  <NextLectureButton
                    courseProgress={courseProgress}
                    isCompleted={isCompleted}
                    nextLecture={nextLecture}
                  />
                )}
              </>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFullscreen(true)}
              >
                <Maximize className="w-4 h-4 mr-2" />
                {t("lecture.view.fullScreen")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Resources - If available */}
      {content.resourcePaths && content.resourcePaths.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-6 py-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            {t("lecture.view.resources")}
          </h3>
          <div className="flex gap-2 flex-wrap">
            {content.resourcePaths.map((resource, index) => (
              <a
                key={index}
                href={resource}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                {t("lecture.view.resourceNumber", { number: index + 1 })}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
